import { useEffect, useMemo, useRef, useState } from 'react'
import { MenuDescription } from './MenuDescription'
import ChatHistoryList from '@features/assistant/components/ChatHistoryList'
import { NavLink, useLocation } from 'react-router-dom'
import { OrganizedTableList, TableSidebarFooter } from '.'
import MenuScrollArea from './MenuScrollArea'
import { EyeOffIcon, HeartIcon, HomeIcon, KebabIcon, PinIcon } from '../../shared/icons/icons'
import { DropdownMenu, type MenuSection } from '@features/shared/components/DropdownMenu'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import { IconButton } from '@features/shared/components/IconButton'
import { AccountIdentity, AccountSignOutFooter } from '@features/auth/components/AccountDrawer'
import { AccountContextSettings } from '@features/auth/components/AccountContextSettings'
import type { SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import { normalizeSearch } from '../logic/tableOrder'
import { useDrawerInUse } from '../hooks/useDrawerInUse'
import { ModuleFlyout } from './ModuleFlyout'
import {
  ADD_TO_FAVORITES_LABEL,
  FavoriteToggle,
  REMOVE_FROM_FAVORITES_LABEL,
  SET_AS_HOME_PAGE_LABEL,
} from './FavoriteToggle'
import FavoritesMenu from './FavoritesMenu'
import { HIDE_LABEL, HiddenToggle, UNHIDE_LABEL } from './HiddenToggle'
import { FavoriteFilterToggle, HiddenFilterToggle } from './MenuFilterToggles'
import { useHiddenChanges, useIsHidden } from '../hooks/useHiddenPages'
import { menuRowPathProps, useMenuFilter, useReleaseHeldRowsWhenIdle } from '../hooks/useMenuFilter'
import { useTableOrganization } from '../hooks'
import { isPathWithin } from '../logic/favorites'
import { Path } from '../../../routes/paths'
import {
  useFavoriteChanges,
  useFavoritesSort,
  useIsFavorite,
  useIsHomePage,
  useSetHomePage,
} from '../hooks/useFavorites'
import { FAVORITES_RAIL_ITEM, type ActiveModule } from '../models'
import { TableFoldersToggle, TableListSearch, TableSortMenu } from './TableListControls'
import {
  getVisibleNavigationSections,
  type AppNavigationItem,
  type AppNavigationModule,
  type AppNavigationSection,
} from '../../../config/appNavigation'
import {
  MENU_HEADER_END_PAD_CLASS,
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  MENU_ROW_END_PAD_CLASS,
  menuIconSlotClass,
  menuIconSlotStates,
} from './menuIconSlots'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'

interface ModuleDrawerProps {
  activeModule: ActiveModule | null
  modules: readonly AppNavigationModule[]
  settingsNavigationPending: boolean
  settingsNavigationError: boolean
  isOpen: boolean
  /** A side panel card beside the page; otherwise a card beside the hovered icon, over the page. */
  isPinned: boolean
  /** The rail is widened over the page, carrying a pinned menu over it too. */
  isFloating?: boolean
  foldersEnabled: boolean
  tableSort: SidebarTableSort
  onToggleFolders: () => void
  onEnableFolders: () => void
  onTableSortChange: (sort: SidebarTableSort) => void
  onTogglePin: (module: ActiveModule) => void
  /** A drawer item was chosen. Keep it stable: the table list is memoized. */
  onItemSelect: () => void
  /** A setting was changed: hold the menu open until explicitly dismissed. */
  onSettingChange?: () => void
  /** Opens a table's settings panel. Keep it stable: the table list is memoized. */
  onOpenTableSettings: (tableId: string) => void
  /**
   * Pinned, with nothing to show for the item the pointer rests on (Home, AI)
   * or the area open (AI): the card stays, with the menu it last showed under
   * a subtle gray veil, out of reach, so the page never moves and the card
   * never changes color much.
   */
  blank?: boolean
  /** The pointer is on this menu's rail item. */
  underPointer?: boolean
  /** Opened by a click on its rail item: it takes the cursor once drawn. */
  focusRequested?: boolean
  onFocusHandled?: () => void
}

/** A text field (or editable text) has the keyboard: its keys are its own. */
function isEditable(element: Element): boolean {
  return (
    (element instanceof HTMLElement && element.isContentEditable) ||
    element.matches('input, textarea, select')
  )
}

// Above the sidebar's own layer, as the table list's menus are.

function ModuleNavMenu({
  sections,
  searchQuery,
  shows,
  onItemSelect,
}: {
  sections: readonly AppNavigationSection[]
  searchQuery: string
  /** The menu's hidden and favorites filters let the page's row show. */
  shows: (path: string) => boolean
  onItemSelect?: () => void
}) {
  const normalizedQuery = normalizeSearch(searchQuery)
  const allItems = sections.flatMap((section) => section.items)
  const items = allItems.filter(
    (item) => item.label.toLocaleLowerCase().includes(normalizedQuery) && shows(item.to)
  )

  if (allItems.length > 0 && items.length === 0) {
    return (
      <p className="px-3 py-2 text-sm text-[color:var(--sidebar-submenu-text-faint)]">
        {normalizedQuery ? `Nothing matches "${searchQuery.trim()}"` : 'No pages to show'}
      </p>
    )
  }

  return (
    <div className="min-w-full space-y-[var(--sidebar-item-inset)]">
      {items.map((item) => (
        <ModuleNavRow key={item.to} item={item} onItemSelect={onItemSelect} />
      ))}
    </div>
  )
}

/**
 * A page in an area's menu, with its options menu, its eye and, in the last
 * slot, its heart: any page can be hidden, a favorite or the Home Page. The
 * eye shows while the page is hidden (the row reads faint), the heart while
 * it's a favorite, and both while the row is hovered; the link fills the row,
 * so the row's hover is the link's too.
 */
// The link's right padding, which keeps its name clear of the icon slots that
// float over the row's end: as many as hold room at rest (none, the heart, or
// the eye and heart), and all three while hovered (menuIconSlots).
const NAV_ROW_END_PAD_CLASS = ['pr-1', 'pr-[1.875rem]', 'pr-[3.5rem]', 'pr-[5.125rem]'] as const
const NAV_ROW_HOVER_END_PAD_CLASS = 'group-hover:pr-[5.125rem]'

function ModuleNavRow({
  item,
  onItemSelect,
}: {
  item: AppNavigationItem
  onItemSelect?: () => void
}) {
  const ItemIcon = item.icon
  const isFavorite = useIsFavorite(item.to)
  const isHomePage = useIsHomePage(item.to)
  const isHidden = useIsHidden(item.to)
  const { add, remove } = useFavoriteChanges()
  const { hide, unhide } = useHiddenChanges()
  const setHomePage = useSetHomePage()
  const [menuOpen, setMenuOpen] = useState(false)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const slots = menuIconSlotStates([menuOpen, isHidden || menuOpen, isFavorite || menuOpen])
  const [menuSlot, hiddenSlot, favoriteSlot] = slots
  const restingSlotCount = slots.filter((slot) => slot !== 'collapsed').length
  const sections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'toggle-favorite',
          label: isFavorite ? REMOVE_FROM_FAVORITES_LABEL : ADD_TO_FAVORITES_LABEL,
          icon: <HeartIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => (isFavorite ? remove(item.to) : add(item.to)),
        },
        {
          type: 'action',
          id: 'toggle-hidden',
          label: isHidden ? UNHIDE_LABEL : HIDE_LABEL,
          icon: <EyeOffIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => (isHidden ? unhide(item.to) : hide(item.to, item.label)),
        },
        {
          type: 'action',
          id: 'set-home-page',
          label: SET_AS_HOME_PAGE_LABEL,
          icon: <HomeIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          omitted: isHomePage,
          onClick: () => setHomePage(item.to, item.label),
        },
      ],
    },
  ]

  return (
    <div ref={rowRef} className="group relative" {...menuRowPathProps(item.to)}>
      <NavLink
        to={item.to}
        onClick={onItemSelect}
        className={({ isActive }) =>
          `flex h-[var(--sidebar-tile-size)] min-w-0 items-center gap-2 rounded-md pl-2 ${NAV_ROW_END_PAD_CLASS[restingSlotCount]} ${NAV_ROW_HOVER_END_PAD_CLASS} text-sm ${
            // Selected and hovered like the rail's items (the sidebar-item tokens).
            isActive
              ? 'bg-[color:var(--sidebar-item-active-bg)] font-medium text-[color:var(--sidebar-item-active-text)] group-hover:bg-[color:var(--sidebar-item-active-hover-bg)]'
              : isHidden
                ? 'text-[color:var(--sidebar-submenu-text-faint)] group-hover:bg-[color:var(--sidebar-item-hover-bg)]'
                : 'text-[color:var(--sidebar-submenu-text-data)] group-hover:bg-[color:var(--sidebar-item-hover-bg)]'
          }`
        }
      >
        {ItemIcon && <ItemIcon size={15} className="shrink-0" aria-hidden="true" />}
        <FadeTruncatedText>{item.label}</FadeTruncatedText>
        <MenuDescription description={item.description} />
      </NavLink>
      <span
        className={`absolute inset-y-0 right-0 ${MENU_ROW_END_PAD_CLASS} ${MENU_ICON_SLOTS_CLASS}`}
      >
        <span className={menuIconSlotClass(menuSlot)}>
          <DropdownMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            sections={sections}
            align="right"
            placement="right-start"
            anchorElementRef={rowRef}
            className="min-w-52"
            trigger={
              <IconButton
                label="Page options"
                className={`${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
              >
                <KebabIcon size={14} aria-hidden="true" />
              </IconButton>
            }
          />
        </span>
        <span className={menuIconSlotClass(hiddenSlot)}>
          <HiddenToggle path={item.to} name={item.label} />
        </span>
        <span className={menuIconSlotClass(favoriteSlot)}>
          <FavoriteToggle path={item.to} variant="row" />
        </span>
      </span>
    </div>
  )
}

export default function ModuleDrawer({
  activeModule,
  modules,
  settingsNavigationPending,
  settingsNavigationError,
  isOpen,
  isPinned,
  isFloating = false,
  foldersEnabled,
  tableSort,
  onToggleFolders,
  onEnableFolders,
  onTableSortChange,
  onTogglePin,
  onItemSelect,
  onSettingChange,
  onOpenTableSettings,
  blank = false,
  underPointer = false,
  focusRequested = false,
  onFocusHandled,
}: ModuleDrawerProps) {
  const module = modules.find((candidate) => candidate.id === activeModule)
  const isTables = module?.drawer === 'table-tree'
  const isChatHistory = module?.drawer === 'chat-history'
  const isSettings = module?.id === 'settings'
  const isMe = module?.id === 'me'
  const isFavorites = activeModule === FAVORITES_RAIL_ITEM
  // An area without a menu (AI), shown pinned: its card, empty under its name.
  const isMenuless = module?.drawer === 'none'
  const favoritesSort = useFavoritesSort()
  const sections = useMemo(() => (module ? getVisibleNavigationSections(module) : []), [module])
  // A search belongs to the module it was typed in; another module starts empty.
  const [search, setSearch] = useState<{ module: ActiveModule | null; query: string }>({
    module: null,
    query: '',
  })
  // Closing the drawer clears its search, so it never reopens quietly filtered.
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen)
    if (!isOpen && search.query) setSearch({ module: null, query: '' })
  }
  // Tables and the AI Assistant's conversations each have a search band.
  const isSearchable = isTables || isChatHistory
  const searchQuery = isSearchable && search.module === activeModule ? search.query : ''
  const { handlers: inUseHandlers, isPointerInside } = useDrawerInUse()
  const contentRef = useRef<HTMLDivElement>(null)
  useReleaseHeldRowsWhenIdle()

  // The pages in this menu, for its hidden and favorites filters (the
  // Favorites menu has none: its rows are all favorites, none hidden).
  const { pathname } = useLocation()
  const { data: tableOrganization } = useTableOrganization()
  const navItems = useMemo(() => sections.flatMap((section) => section.items), [sections])
  const menuPaths = useMemo(
    () =>
      isFavorites || isMenuless
        ? []
        : isTables
          ? (tableOrganization?.tables ?? []).map((table) => Path.tableRecords(table.id))
          : navItems.map((item) => item.to),
    [isFavorites, isMenuless, isTables, navItems, tableOrganization]
  )
  const currentNavPath = navItems.find((item) => isPathWithin(pathname, item.to))?.to ?? null
  const menuFilter = useMenuFilter(activeModule ?? '', menuPaths, {
    searching: searchQuery.trim() !== '',
    current: isTables ? null : currentNavPath,
  })

  const content = (
    <>
      <div
        // One rail row tall, so beside the rail its bottom border runs on from
        // the line under Home and the rows line up with the rail's. The Me
        // menu's band is taller by its email line (AccountIdentity). Its title
        // starts where the rows' text does (the list's inset and a row's own).
        className={`flex ${isMe ? 'h-[calc(var(--sidebar-row-height)+1rem)]' : 'h-[var(--sidebar-row-height)]'} shrink-0 items-center justify-between gap-2 border-b border-[color:var(--sidebar-submenu-border)] pl-3 ${MENU_HEADER_END_PAD_CLASS}`}
      >
        {isMe ? (
          <AccountIdentity onEditPhoto={onSettingChange} />
        ) : (
          // An area's menu names it and, like its rail item, describes it.
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-[color:var(--sidebar-submenu-text-default)]">
              {isFavorites ? 'Favorites' : (module?.label ?? '')}
            </h2>
            {!isFavorites && <MenuDescription description={module?.description} />}
          </div>
        )}
        {activeModule && (
          <div className={MENU_ICON_SLOTS_CLASS}>
            {/* The filters come and go (once the menu holds a hidden page, or a
                favorite), so they lead: folders, sort and the pin never move. */}
            {menuFilter.hasHidden && (
              <HiddenFilterToggle
                value={menuFilter.hiddenFilter}
                onChange={menuFilter.setHiddenFilter}
              />
            )}
            {menuFilter.hasFavorites && (
              <FavoriteFilterToggle
                value={menuFilter.favoriteFilter}
                onChange={menuFilter.setFavoriteFilter}
              />
            )}
            {isTables && (
              <>
                <TableFoldersToggle foldersEnabled={foldersEnabled} onToggle={onToggleFolders} />
                <TableSortMenu sort={tableSort} onChange={onTableSortChange} />
              </>
            )}
            {isFavorites && (
              <TableSortMenu
                sort={favoritesSort.sort}
                onChange={favoritesSort.setSort}
                label="Sort favorites"
              />
            )}
            <IconButton
              label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
              onClick={() => onTogglePin(activeModule)}
              className={`${MENU_ICON_SLOT_CLASS} transition-colors ${
                // Pinned in info blue, as a pinned column's pin is.
                isPinned
                  ? 'text-[color:var(--accent-info)] hover:text-[color:var(--text-info)]'
                  : 'text-[color:var(--sidebar-submenu-text-faint)] hover:text-[color:var(--sidebar-submenu-text-subtle)]'
              }`}
              aria-pressed={isPinned}
            >
              <PinIcon size={14} filled={isPinned} aria-hidden="true" />
            </IconButton>
          </div>
        )}
      </div>

      {/* Account context stays together beneath the identity, apart from pages. */}
      {isMe && <AccountContextSettings onInteract={onSettingChange} />}

      {isSearchable && (
        <TableListSearch
          label={isChatHistory ? 'Conversations' : module.label}
          value={searchQuery}
          onChange={(query) => setSearch({ module: activeModule, query })}
        />
      )}

      <MenuScrollArea
        // SIDEBAR_LIST_VIEWPORT_ATTRIBUTE: a dragged row stays in the part showing here.
        data-sidebar-list-viewport=""
        // Pinned, the list fills the card; hovering, the card is as tall as the
        // list, which scrolls past the room beside the rail.
        className={isPinned ? 'flex-1' : 'flex-auto'}
      >
        {isFavorites ? (
          <FavoritesMenu onSelect={onItemSelect} />
        ) : isTables ? (
          <div className="min-w-full">
            <OrganizedTableList
              foldersEnabled={foldersEnabled}
              sort={tableSort}
              searchQuery={searchQuery}
              onSelect={onItemSelect}
              onOpenTableSettings={onOpenTableSettings}
            />
          </div>
        ) : isChatHistory ? (
          <ChatHistoryList searchQuery={searchQuery} onSelect={onItemSelect} />
        ) : isSettings && settingsNavigationPending ? null : isSettings &&
          settingsNavigationError ? (
          <p className="px-3 py-2 text-sm text-danger">Settings pages couldn't be loaded.</p>
        ) : module && !isMenuless ? (
          <ModuleNavMenu
            sections={sections}
            searchQuery={searchQuery}
            shows={menuFilter.shows}
            onItemSelect={onItemSelect}
          />
        ) : null}
      </MenuScrollArea>
      {isTables && (
        <TableSidebarFooter
          foldersEnabled={foldersEnabled}
          onEnableFolders={onEnableFolders}
          onAddTable={onItemSelect}
        />
      )}
      {isMe && <AccountSignOutFooter />}
    </>
  )

  // Opened by a click on its rail item: the cursor goes to its search, or to
  // its first page, so the keyboard can take it from there.
  useEffect(() => {
    if (!focusRequested || !isOpen || blank) return
    const container = contentRef.current
    const target =
      container?.querySelector<HTMLElement>('input[type="search"]') ??
      container?.querySelector<HTMLElement>('[data-sidebar-list-viewport] :is(a[href], button)')
    target?.focus()
    onFocusHandled?.()
  }, [blank, focusRequested, isOpen, onFocusHandled])

  // Type to search: with the pointer on the Tables menu (or its rail item) and
  // no text field, nor anything on the page, holding the keyboard, a letter
  // typed goes into its search, and the cursor with it. It never takes keys
  // from a field or from the page (the table grid reads them too).
  useEffect(() => {
    if (!isOpen || !isTables || blank) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.isComposing || event.key.length !== 1 || event.key === ' ') return
      if (!underPointer && !isPointerInside()) return
      const focused = document.activeElement
      if (
        focused &&
        focused !== document.body &&
        (isEditable(focused) || !focused.closest('[data-app-sidebar]'))
      ) {
        return
      }
      const input = contentRef.current?.querySelector<HTMLInputElement>('input[type="search"]')
      if (!input) return
      event.preventDefault()
      setSearch((current) => ({
        module: activeModule,
        query: (current.module === activeModule ? current.query : '') + event.key,
      }))
      input.focus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeModule, blank, isOpen, isPointerInside, isTables, underPointer])

  // Blank, nothing in the menu takes the pointer or focus (`inert` isn't a
  // prop React passes through on a plain element here).
  useEffect(() => {
    contentRef.current?.toggleAttribute('inert', blank)
  }, [blank])

  // Pinned, it shows the current area's menu; an area without one (AI) keeps
  // the card, blank, so the page never moves (GlobalSidebar).
  if (isPinned && !isOpen) return null

  // Right after the rail (left-full), so a rail widening on hover carries a
  // pinned card along over the page (GlobalSidebar). Pinned or hovering, it's
  // the one ModuleFlyout, so pinning and unpinning restyle it in place.
  return (
    <ModuleFlyout
      activeModule={activeModule}
      isOpen={isOpen}
      pinned={isPinned}
      floating={isFloating}
      width="var(--sidebar-menu-width)"
      {...inUseHandlers}
    >
      {/* Blank, the menu stays in the card as it was, out of reach (inert),
          so it comes back as it was (a long list isn't drawn again, and keeps
          its scroll). */}
      <div ref={contentRef} className="contents" aria-hidden={blank || undefined}>
        {content}
      </div>
      {blank && (
        <div
          aria-hidden="true"
          // Over everything in the menu, its hearts and pins too.
          className="pointer-events-none absolute inset-0 z-20 bg-[color:var(--sidebar-menu-idle-veil)]"
        />
      )}
    </ModuleFlyout>
  )
}
